<script setup lang="ts">
    import type { FrontendFieldDefinition } from '@hmmh/storyblok-forms/client';
    import type { ComputedRef } from 'vue';
    import type { FormInputs } from '~/utils/formUtils';
    import SelectInput, { type SelectOption } from '~/components/shared/SelectInput.vue';
    import { isRequiredField, useFormFieldId } from './form-field-utils';

    const props = defineProps<{ field: FrontendFieldDefinition }>();
    const inputId = useFormFieldId();

    const formInputs = inject<Ref<FormInputs>>('formInputs');
    const activeValues = inject<ComputedRef<Record<string, unknown>>>('formActiveValues');

    const model = computed<string>({
        get: () =>
            String(
                (props.field.type === 'conditional' ? activeValues?.value[props.field.name] : undefined) ??
                    formInputs?.value[props.field.name]?.value ??
                    ''
            ),
        set: (value) => {
            const input = formInputs?.value[props.field.name];

            if (!input || props.field.disabled) return;

            input.value = value;
        }
    });

    const options = computed<SelectOption[]>(() => [
        {
            label: props.field.placeholder || '—',
            value: '',
            disabled: true
        },
        ...(props.field.options ?? []).map((option) => ({
            label: option.value,
            value: option.key
        }))
    ]);

    const validationErrors = computed(() => formInputs?.value[props.field.name]?.errors ?? []);
</script>

<template>
    <SelectInput
        :id="inputId"
        v-model="model"
        :label="field.label"
        :options="options"
        :required="isRequiredField(field)"
        :disabled="field.disabled"
        :validation-errors="validationErrors"
    />
</template>
